import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Spin from 'components/Spin';
import { Formik, Form, Field } from 'formik';
import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { EMPTY, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import DialogTitle from 'v2/components/Shared/DialogTitle';
import { ERefundMotiveId } from 'v2/interfaces/requestManagement/refund';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';
import subscriptionsService from 'v2/services/subscriptions';

import { SelectFormField } from '../FormFields/SelectFormField';
import { TextFormField } from '../FormFields/TextFormField';
import Transition from '../Transition';
import { ContractCancellation } from './ContractCancellation';
import useStyles from './styles';
import { RefundPartnerRequestSchema } from './validations';

import type { FormikProps } from 'formik';

export interface IPartnerRefundReason {
  motive: string;
  detail?: string;
  invoiceId: string;
  cancelContractMotive?: number;
  cancelContractDetail?: string;
  cancelContract?: boolean;
  contractId?: number;
}

export interface IPartnerRefundReasonModal {
  invoiceId: string;
}

interface IProps {
  open: boolean;
  invoiceId: string | number;
  contractId?: number;
  onClose: () => void;
  onFinish: (refundReason: IPartnerRefundReason) => void;
}

const OTHER_CONTRACT_CANCEL_REASON = 1;

export default function PartnerRefundRequestModal({ open, invoiceId, contractId, onClose, onFinish }: IProps) {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [refundReason, setRefundReason] = useState<IPartnerRefundReason>();

  const [motives] = useObservable(() => refundRequestFactory.getRefundMotive(), []);
  const [loading] = useObservable(() => refundRequestFactory.loading(), []);

  const SUBSCRIPTIONS_NEXT_STATUS_NAME = 4;

  const [onSubmit] = useCallbackObservable((values: IPartnerRefundReason, { setSubmitting, resetForm }: FormikProps<IPartnerRefundReason>) => {
    setSubmitting(true);

    if (values.motive !== ERefundMotiveId.OTHER_REASON) {
      delete values.detail;
    }

    return refundRequestFactory.sendPartnerRefundRequest(values).pipe(
      tap((response) => {
        const refundReason: IPartnerRefundReason = {
          ...values,
          ...{ success: response.success, motive: response.request_motive_id, detail: response.description, invoiceId: response.invoice_id },
        };
        setRefundReason(refundReason);
        if (values.cancelContract) {
          cancelSubscription(values);
        }
        setSubmitting(false);
        resetForm();
        toast.success('Solicitação realizada com sucesso!');
        onComplete();
      }),
      catchError((err) => {
        setSubmitting(false);
        toast.error(err);
        return of(true);
      })
    );
  }, []);

  const onComplete = useCallback(() => {
    onFinish(refundReason);
    setRefundReason(null);
  }, [onFinish, refundReason]);

  const onHandleClose = useCallback(
    (resetForm) => {
      setRefundReason(null);
      onClose();
      resetForm();
    },
    [onClose]
  );

  const [cancelSubscription] = useCallbackObservable(
    (values: IPartnerRefundReason) => {
      const payload = { reason_id: values.cancelContractMotive, reason_description: values.cancelContractDetail };

      if (values.cancelContractMotive !== OTHER_CONTRACT_CANCEL_REASON) {
        delete payload.reason_description;
      }

      return subscriptionsService.cancelSubscription(contractId, payload).pipe(
        catchError((err) => {
          console.error(err);
          toast.error('Falha ao completar o cancelamento do contrato');
          return of(EMPTY);
        })
      );
    },
    [contractId]
  );

  return (
    <Formik<IPartnerRefundReason>
      validationSchema={RefundPartnerRequestSchema}
      initialValues={{ motive: '', detail: '', invoiceId: String(invoiceId), cancelContract: false, cancelContractDetail: '', contractId }}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, submitForm, values, resetForm, dirty, isValid }) => {
        const otherReasonSelected = values.motive === ERefundMotiveId.OTHER_REASON;
        const shouldDisableConfirmBtn = !isValid || !dirty || isSubmitting;

        const checkBoxFormat = (motives || []).map((item) => ({
          label: item.description,
          value: item.id,
        }));

        return (
          <Form>
            <Dialog
              TransitionComponent={Transition}
              aria-labelledby='customized-dialog-title'
              open={open}
              onClose={() => onHandleClose(resetForm)}
              fullScreen={fullScreen}
              fullWidth
            >
              <DialogTitle id='customized-dialog-title' onClose={() => onHandleClose(resetForm)}>
                <Typography variant='h6'>Deseja reembolsar essa fatura?</Typography>
              </DialogTitle>
              <DialogContent className={classes.dialogContent} dividers>
                {loading || isSubmitting ? (
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Spin />
                  </div>
                ) : (
                  <div>
                    <Field
                      className={otherReasonSelected ? classes.motiveSelect : undefined}
                      id='motive'
                      name='motive'
                      options={checkBoxFormat}
                      component={SelectFormField}
                      fullWidth
                      label='Selecione o motivo do reembolso:'
                    />
                    {otherReasonSelected && (
                      <Field
                        id='detail'
                        name='detail'
                        multiline
                        fullWidth
                        InputProps={{ rows: 3, disableUnderline: true }}
                        InputLabelProps={{ shrink: true }}
                        placeholder='Descreva o motivo do reembolso dessa fatura. (Lembre-se de que essa informação será direcionada ao cliente.)'
                        component={TextFormField}
                      />
                    )}
                    {!!contractId && <ContractCancellation contractId={contractId} />}
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button color='default' onClick={() => onHandleClose(resetForm)}>
                  Agora não
                </Button>
                <Button
                  onClick={submitForm}
                  type='submit'
                  variant='contained'
                  color='secondary'
                  disabled={shouldDisableConfirmBtn}
                  className={classes.button}
                >
                  Confirmar
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
}
